import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Avatar,
  Stack,
  useMediaQuery,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { SubjectService } from '../../services/subjectService';
import { AttendanceService } from '../../services/attendanceService';
import { Subject, Student } from '../../types';

interface StudentSubjectAttendance {
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  totalSessions: number;
  presentSessions: number;
  attendanceRate: number;
}

interface StudentAttendanceRecord {
  date: string;
  status: 'Present' | 'Absent';
  remarks: string;
  markedBy: string;
  markedByName: string;
  subjectName: string;
  subjectCode: string;
}

export function StudentSubjectAttendance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [studentSubjectAttendance, setStudentSubjectAttendance] = useState<StudentSubjectAttendance[]>([]);
  const [detailedRecords, setDetailedRecords] = useState<StudentAttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudentInfo, setSelectedStudentInfo] = useState<Student | null>(null);

  useEffect(() => {
    loadSubjectsAndStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      loadStudentAttendance();
    }
  }, [selectedStudent, startDate, endDate]);

  const loadSubjectsAndStudents = async () => {
    try {
      setLoading(true);
      const result = await SubjectService.getSubjects(100);
      setSubjects(result.data);

      // Extract all unique students from all subjects
      const studentsSet = new Map<string, Student>();
      result.data.forEach(subject => {
        subject.students.forEach(student => {
          if (!studentsSet.has(student.id)) {
            studentsSet.set(student.id, student);
          }
        });
      });
      
      setAllStudents(Array.from(studentsSet.values()));
    } catch (error) {
      console.error('Error loading subjects and students:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentAttendance = async () => {
    if (!selectedStudent) return;

    try {
      setLoading(true);
      const student = allStudents.find(s => s.id === selectedStudent);
      setSelectedStudentInfo(student || null);

      // Get attendance for all subjects this student is enrolled in
      const studentSubjects = subjects.filter(subject => 
        subject.students.some(s => s.id === selectedStudent)
      );

      const attendancePromises = studentSubjects.map(async (subject) => {
        try {
          const result = await AttendanceService.getStudentSubjectAttendanceFromSubjectsCollection(
            subject.id,
            selectedStudent,
            startDate || undefined,
            endDate || undefined
          );
          return {
            subjectId: result.subjectId,
            subjectName: result.subjectName,
            subjectCode: result.subjectCode,
            totalSessions: result.totalSessions,
            presentSessions: result.presentSessions,
            attendanceRate: result.attendanceRate
          };
        } catch (error) {
          console.warn(`Failed to get attendance for subject ${subject.id}:`, error);
          return {
            subjectId: subject.id,
            subjectName: subject.name,
            subjectCode: subject.code,
            totalSessions: 0,
            presentSessions: 0,
            attendanceRate: 0
          };
        }
      });

      const attendanceResults = await Promise.all(attendancePromises);
      setStudentSubjectAttendance(attendanceResults);

      // Get detailed attendance records for timeline view
      const detailedRecordsPromises = studentSubjects.map(async (subject) => {
        try {
          const result = await AttendanceService.getStudentSubjectAttendanceFromSubjectsCollection(
            subject.id,
            selectedStudent,
            startDate || undefined,
            endDate || undefined
          );
          return result.attendanceRecords.map(record => ({
            ...record,
            subjectName: result.subjectName,
            subjectCode: result.subjectCode
          }));
        } catch (error) {
          return [];
        }
      });

      const allDetailedRecords = (await Promise.all(detailedRecordsPromises)).flat();
      // Sort by date
      allDetailedRecords.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setDetailedRecords(allDetailedRecords);

    } catch (error) {
      console.error('Error loading student attendance:', error);
      setStudentSubjectAttendance([]);
      setDetailedRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getOverallAttendanceRate = () => {
    if (studentSubjectAttendance.length === 0) return 0;
    const totalSessions = studentSubjectAttendance.reduce((sum, subject) => sum + subject.totalSessions, 0);
    const totalPresent = studentSubjectAttendance.reduce((sum, subject) => sum + subject.presentSessions, 0);
    return totalSessions > 0 ? (totalPresent / totalSessions) * 100 : 0;
  };

  const overallRate = getOverallAttendanceRate();

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Student Subject-wise Attendance
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Student</InputLabel>
              <Select
                value={selectedStudent}
                label="Select Student"
                onChange={(e) => setSelectedStudent(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a student</em>
                </MenuItem>
                {allStudents.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.name} ({student.rollNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadStudentAttendance}
              disabled={!selectedStudent || loading}
            >
              Load Data
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {selectedStudent && selectedStudentInfo && (
        <>
          {/* Student Info and Overall Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedStudentInfo.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Roll Number: {selectedStudentInfo.rollNumber}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Enrolled in {studentSubjectAttendance.length} subjects
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <AssessmentIcon 
                      sx={{ 
                        fontSize: 56,
                        color: overallRate >= 75 ? 'success.main' : 'error.main'
                      }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" color="textSecondary">
                        Overall Attendance
                      </Typography>
                      <Typography 
                        variant="h4" 
                        color={overallRate >= 75 ? 'success.main' : 'error.main'}
                      >
                        {overallRate.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={overallRate}
                        color={overallRate >= 75 ? 'success' : 'error'}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Subject-wise Attendance Table */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Subject-wise Attendance Summary
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : studentSubjectAttendance.length === 0 ? (
              <Alert severity="info">
                No attendance data found for the selected student and date range.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Subject</TableCell>
                      <TableCell align="center">Total Sessions</TableCell>
                      <TableCell align="center">Present</TableCell>
                      <TableCell align="center">Absent</TableCell>
                      <TableCell align="center">Attendance Rate</TableCell>
                      <TableCell align="center">Progress</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentSubjectAttendance.map((subject) => (
                      <TableRow key={subject.subjectId} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SchoolIcon color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {subject.subjectName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {subject.subjectCode}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={subject.totalSessions}
                            color="info"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={subject.presentSessions}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={subject.totalSessions - subject.presentSessions}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${subject.attendanceRate.toFixed(1)}%`}
                            color={getAttendanceColor(subject.attendanceRate)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ width: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={subject.attendanceRate}
                              color={getAttendanceColor(subject.attendanceRate)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Recent Attendance Records */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Recent Attendance Records
            </Typography>

            {detailedRecords.length === 0 ? (
              <Alert severity="info">
                No detailed attendance records found.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {detailedRecords.slice(0, 10).map((record, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent sx={{ py: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(record.date)}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="textSecondary">
                            {record.subjectCode} - {record.subjectName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Chip
                            label={record.status}
                            color={record.status === 'Present' ? 'success' : 'error'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="textSecondary">
                            Marked by: {record.markedByName || record.markedBy}
                          </Typography>
                          {record.remarks && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Remarks: {record.remarks}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
                {detailedRecords.length > 10 && (
                  <Typography variant="body2" color="textSecondary" align="center">
                    Showing 10 of {detailedRecords.length} records
                  </Typography>
                )}
              </Stack>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
} 